import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Users, Mail, Send, Pencil, Trash2, ChevronDown, ChevronUp, Paperclip } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminNewsletterTab() {
  const [subscribers, setSubscribers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [view, setView] = useState("subscribers"); // subscribers | send | campaigns
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [sendForm, setSendForm] = useState({ subject: "", body: "", topics: ["general"], attachment: null });
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const fetchSubscribers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/newsletter/subscribers`, { withCredentials: true });
      setSubscribers(data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/newsletter/campaigns`, { withCredentials: true });
      setCampaigns(data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchSubscribers(); fetchCampaigns(); }, [fetchSubscribers, fetchCampaigns]);

  const openEdit = (sub) => {
    setEditForm({ ...sub });
    setEditDialog(sub);
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/newsletter/subscribers/${editDialog.id}`, editForm, { withCredentials: true });
      setEditDialog(null);
      fetchSubscribers();
    } catch (err) { console.error(err); }
  };

  const deleteSub = async (id) => {
    if (!window.confirm("Abonnent wirklich löschen?")) return;
    await axios.delete(`${API}/newsletter/subscribers/${id}`, { withCredentials: true });
    fetchSubscribers();
  };

  const handleSend = async () => {
    setSending(true);
    setSendResult(null);
    try {
      const fd = new FormData();
      fd.append("subject", sendForm.subject);
      fd.append("body", sendForm.body);
      fd.append("topics", JSON.stringify(sendForm.topics));
      if (sendForm.attachment) fd.append("attachment", sendForm.attachment);

      const { data } = await axios.post(`${API}/newsletter/send`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSendResult(data.message);
      fetchCampaigns();
    } catch (err) {
      setSendResult("Fehler beim Versenden");
    } finally {
      setSending(false);
    }
  };

  const toggleSendTopic = (t) => {
    setSendForm(prev => ({
      ...prev,
      topics: prev.topics.includes(t) ? prev.topics.filter(x => x !== t) : [...prev.topics, t],
    }));
  };

  const confirmed = subscribers.filter(s => s.status === "confirmed").length;
  const pending = subscribers.filter(s => s.status === "pending").length;

  const statusLabel = { confirmed: "Bestätigt", pending: "Ausstehend", unsubscribed: "Abgemeldet" };
  const statusColor = { confirmed: "text-green-400", pending: "text-yellow-400", unsubscribed: "text-red-400" };

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4 text-center">
          <p className="text-2xl font-heading text-[#C5993A]">{confirmed}</p>
          <p className="text-xs text-[#8899AA]">Bestätigt</p>
        </div>
        <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4 text-center">
          <p className="text-2xl font-heading text-yellow-400">{pending}</p>
          <p className="text-xs text-[#8899AA]">Ausstehend</p>
        </div>
        <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4 text-center">
          <p className="text-2xl font-heading text-white">{campaigns.length}</p>
          <p className="text-xs text-[#8899AA]">Kampagnen</p>
        </div>
      </div>

      {/* Tab buttons */}
      <div className="flex gap-2 mb-6">
        {[["subscribers", "Abonnenten", Users], ["send", "Newsletter senden", Send], ["campaigns", "Verlauf", Mail]].map(([key, label, Icon]) => (
          <Button
            key={key}
            onClick={() => setView(key)}
            variant={view === key ? "default" : "ghost"}
            className={view === key ? "bg-[#C5993A] text-white border-0 text-xs" : "text-[#8899AA] text-xs"}
            size="sm"
          >
            <Icon className="w-3 h-3 mr-1" /> {label}
          </Button>
        ))}
      </div>

      {/* Subscribers list */}
      {view === "subscribers" && (
        <div className="space-y-2" data-testid="nl-subscribers-list">
          {subscribers.length === 0 ? (
            <p className="text-[#3A4F60] text-center py-8">Noch keine Abonnenten.</p>
          ) : subscribers.map(sub => (
            <div key={sub.id} className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  {sub.title && `${sub.title} `}{sub.first_name} {sub.last_name}
                  <span className={`ml-2 text-xs ${statusColor[sub.status]}`}>{statusLabel[sub.status]}</span>
                </p>
                <p className="text-[#8899AA] text-xs truncate">{sub.email}</p>
                <div className="flex gap-2 mt-1">
                  {(sub.topics || []).map(t => (
                    <span key={t} className="text-[10px] bg-[#C5993A]/10 text-[#C5993A] px-2 py-0.5 rounded">
                      {t === "general" ? "Allgemein" : "Events"}
                    </span>
                  ))}
                  <span className="text-[10px] text-[#3A4F60]">
                    {sub.salutation === "informal" ? "Du" : "Sie"}
                  </span>
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button onClick={() => openEdit(sub)} variant="ghost" size="sm" className="text-[#8899AA] hover:text-[#C5993A]">
                  <Pencil className="w-3 h-3" />
                </Button>
                <Button onClick={() => deleteSub(sub.id)} variant="ghost" size="sm" className="text-[#8899AA] hover:text-red-400">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Send newsletter */}
      {view === "send" && (
        <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-6 space-y-4" data-testid="nl-send-form">
          <div>
            <label className="block text-xs text-[#8899AA] mb-1">Betreff *</label>
            <Input value={sendForm.subject} onChange={(e) => setSendForm({...sendForm, subject: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm focus:border-[#C5993A]" />
          </div>
          <div>
            <label className="block text-xs text-[#8899AA] mb-1">Inhalt *</label>
            <Textarea value={sendForm.body} onChange={(e) => setSendForm({...sendForm, body: e.target.value})} rows={8} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm focus:border-[#C5993A] resize-none" placeholder="Newsletter-Text (wird personalisiert mit Anrede versendet)..." />
          </div>
          <div>
            <label className="block text-xs text-[#8899AA] mb-2">Zielgruppe *</label>
            <div className="flex gap-4">
              {[["general", "Allgemeiner Newsletter"], ["events", "Veranstaltungen"]].map(([val, label]) => (
                <label key={val} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={sendForm.topics.includes(val)} onCheckedChange={() => toggleSendTopic(val)} className="border-[#3A4F60] data-[state=checked]:bg-[#C5993A] data-[state=checked]:border-[#C5993A]" />
                  <span className="text-xs text-[#F0F4F8]">{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#8899AA] mb-1">PDF-Anhang (optional)</label>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => setSendForm({...sendForm, attachment: e.target.files[0] || null})}
              className="text-xs text-[#8899AA] file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-[#C5993A]/20 file:text-[#C5993A] hover:file:bg-[#C5993A]/30"
              data-testid="nl-attachment-input"
            />
            {sendForm.attachment && (
              <p className="text-xs text-[#C5993A] mt-1 flex items-center gap-1"><Paperclip className="w-3 h-3" /> {sendForm.attachment.name}</p>
            )}
          </div>
          {sendResult && <p className="text-sm text-[#C5993A]">{sendResult}</p>}
          <Button
            data-testid="nl-send-button"
            onClick={handleSend}
            disabled={sending || !sendForm.subject || !sendForm.body || sendForm.topics.length === 0}
            className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 text-sm"
          >
            {sending ? "Wird versendet..." : `Newsletter an ${confirmed} Empfänger senden`}
          </Button>
        </div>
      )}

      {/* Campaign history */}
      {view === "campaigns" && (
        <div className="space-y-2">
          {campaigns.length === 0 ? (
            <p className="text-[#3A4F60] text-center py-8">Noch keine Kampagnen.</p>
          ) : campaigns.map(c => (
            <div key={c.id} className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-medium">{c.subject}</p>
                  <div className="flex gap-3 text-xs text-[#8899AA] mt-1">
                    <span>{c.sent_count} Empfänger</span>
                    {c.attachment_name && <span className="flex items-center gap-1"><Paperclip className="w-3 h-3" />{c.attachment_name}</span>}
                    <span>{new Date(c.sent_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit subscriber dialog */}
      <Dialog open={!!editDialog} onOpenChange={(o) => { if (!o) setEditDialog(null); }}>
        <DialogContent className="bg-[#12202f] border-[#3A4F60]/50 text-white max-w-md" aria-describedby="edit-sub-desc">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Abonnent bearbeiten</DialogTitle>
            <p id="edit-sub-desc" className="sr-only">Abonnent-Formular</p>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div>
              <label className="block text-xs text-[#8899AA] mb-1">Anrede</label>
              <select value={editForm.salutation || "formal"} onChange={(e) => setEditForm({...editForm, salutation: e.target.value})} className="w-full h-9 rounded-md border border-[#3A4F60]/50 bg-[#0C1620] text-white px-3 text-sm">
                <option value="formal">Formell (Sie)</option>
                <option value="informal">Persönlich (Du)</option>
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs text-[#8899AA] mb-1">Titel</label>
                <Input value={editForm.title || ""} onChange={(e) => setEditForm({...editForm, title: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" />
              </div>
              <div>
                <label className="block text-xs text-[#8899AA] mb-1">Vorname</label>
                <Input value={editForm.first_name || ""} onChange={(e) => setEditForm({...editForm, first_name: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" />
              </div>
              <div>
                <label className="block text-xs text-[#8899AA] mb-1">Nachname</label>
                <Input value={editForm.last_name || ""} onChange={(e) => setEditForm({...editForm, last_name: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-[#8899AA] mb-1">E-Mail</label>
              <Input value={editForm.email || ""} onChange={(e) => setEditForm({...editForm, email: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" />
            </div>
            <div>
              <label className="block text-xs text-[#8899AA] mb-1">Status</label>
              <select value={editForm.status || "confirmed"} onChange={(e) => setEditForm({...editForm, status: e.target.value})} className="w-full h-9 rounded-md border border-[#3A4F60]/50 bg-[#0C1620] text-white px-3 text-sm">
                <option value="confirmed">Bestätigt</option>
                <option value="pending">Ausstehend</option>
                <option value="unsubscribed">Abgemeldet</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog(null)} className="text-[#8899AA] text-sm">Abbrechen</Button>
            <Button onClick={saveEdit} className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 text-sm">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
